import React, { useState, useRef, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import gsap from 'gsap';
import VideoPlayer from '../VideoPlayer';
import TabList from '../Tablist';
import styles from './ActivityOrbitalMotion.module.scss';
import Astronaut from '../weightOnOtherWorlds/svgs/Astronaut';
import Tether from '../weightOnOtherWorlds/svgs/Tether';

const ActivityOrbitalMotion = () => {
  const activityTabs= [
    {
      tabTitle: 'First Law',
      tabId: 'law1',
      tabpanelId: 'law1panel',
      videoUrl: 'video/orbital-motion-first-law.mp4',
      boldText: 'Kepler’s First Law describes the shape of an orbit.',
      bodyText: 'The orbit of a planet around the Sun (or a satellite around a planet) is not a perfect circle. It is an ellipse—a “flattened” circle. The Sun (or the center of the planet) occupies one focus of the ellipse. A focus is one of the two internal points that help determine the shape of an ellipse. The distance from one focus to any point on the ellipse and then back to the second focus is always the same.',
    },
    {
      tabTitle: 'Second Law',
      tabId: 'law2',
      tabpanelId: 'law2panel',
      videoUrl: 'video/orbital-motion-second-law.mp4',
      boldText: 'Kepler’s Second Law describes the way an object’s speed varies along its orbit.',
      bodyText: 'A planet’s orbital speed changes, depending on how far the planet is from the Sun. The closer it is to the Sun, the stronger the Sun’s gravitational pull on it, and the faster it moves. The farther it is from the Sun, the weaker the Sun’s gravitational pull, and the slower it moves. The same is true for an orbiting satellite. The closer the satellite is in its orbital path to the planet, the faster it moves.',
    },
    {
      tabTitle: 'Third Law',
      tabId: 'law3',
      tabpanelId: 'law3panel',
      videoUrl: 'video/orbital-motion-third-law.mp4',
      boldText: 'Kepler’s Third Law compares the motion of objects in orbits of different sizes.',
      bodyText: 'A planet farther from the Sun not only has a longer path than a closer planet, but it also moves slower, because the Sun’s gravitational pull on it is weaker. Therefore, the larger a planet’s orbit, the longer the planet takes to complete it. The same is true for orbiting satellites. A satellite orbiting farther away from a planet moves slower than a satellite orbiting closer.',
    },
  ];

    // Astronaut Animation
    const astronautRef = useRef();
    const tetherRef = useRef();
    const [astronautTimeline] = useState(gsap.timeline());
    useEffect(() => {
      astronautTimeline
        .to(astronautRef.current, {
          rotation: 40,
          duration: 6,
          yoyo: true,
          repeat: -1,
          ease: 'power1.inOut',
        })
        .to(
          tetherRef.current,
          {
            rotation: 3,
            transformOrigin: 'left bottom',
            duration: 6,
            yoyo: true,
            repeat: -1,
            ease: 'power1.inOut',
          },
          0
        )
        .to(
          [astronautRef.current, tetherRef.current],
          {
            y: -3,
            duration: 4,
            yoyo: true,
            repeat: -1,
            ease: 'power1.inOut',
          },
          0
        );
      //eslint-disable-next-line
    }, [astronautRef, tetherRef]);  

  const [activeTab, setActiveTab] = useState('law1');
  
	return (
		<main className={`${styles.activityContainer}`}>
			<h1 className={`srOnly`}>
				Orbital Motion - Understand the movements of satellites.
			</h1>
      <TabList tabs={activityTabs} activeTab={activeTab} clickHandler={setActiveTab}/>

      <div className={`relPos ${styles.panelBg}`}>
        <div className={`${styles.astronautContainer}`}>
          <div className={`${styles.tether}`} ref={tetherRef}>
            <Tether />
          </div>
          <div className={`${styles.astronaut}`} ref={astronautRef}>
            <Astronaut />
          </div>
        </div>
        <TransitionGroup>
          <CSSTransition
              key={activeTab}
              in={true}
              appear={true}
              timeout={300}
              classNames="short-fade"
            >
            <div>
            { activityTabs.map(({ tabId, videoUrl }) => {
              if (tabId === activeTab) {
                return (
                  <VideoPlayer 
                    key={tabId} 
                    videoUrl={videoUrl}
                    videoPlaying={true}
                    loop={true}
                  />
                ) } else return null; })}
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
      
      { activityTabs.map(({ tabId, boldText, bodyText }) => {
        if (tabId === activeTab) {
        return (
          <div id={`${tabId}panel`} key={tabId}>
            <div className={`${styles.textContainer} blueTextContainer`} role="alert" aria-live="assertive">
              <p className={`extrabold`}>{boldText}</p>
              <p>{bodyText}</p>
            </div>
          </div>
        ) } else return null; })}
		</main>
	)
}

export default ActivityOrbitalMotion;
